import MojitoServices from 'mojito/services';

import {mergeTo} from '#core/utils/config-utils.js';

import {
    EmptyContentHeadMetaFactory,
    EventHeadMetaFactory,
    HomePageHeadMetaFactory,
    InplayHeadMetaFactory,
    PriceBoostHeadMetaFactory,
    RaceMeetingHeadMetaFactory,
    FutureRaceHeadMetaFactory,
    SingleLeagueHeadMetaFactory,
    SportCouponHeadMetaFactory,
    SportHeadMetaFactory,
    VirtualsHeadMetaFactory,
} from '#core/application/seo/head-meta-factory.js';

import {AbstractVisualFeature, allVisuals} from '#core/application/modules/visual-feature.js';

import {sportsContentFactory} from './sports-content.factory';

import PALETTE_SCHEMA from './sports-content.palette-schema.yaml';
import SCHEMA from './sports-content.schema.yaml';

const {GROUP_TYPES: MARKET_GROUP_TYPES} = MojitoServices.SportsContent.MarketGroups.types;

class SportsContentFeature extends AbstractVisualFeature {
    get name() {
        return 'Sports Content';
    }

    get configSchema() {
        return SCHEMA;
    }

    get paletteSchema() {
        return PALETTE_SCHEMA;
    }

    get couponConfig() {
        return this.config.couponConfig;
    }

    get eventGroupsConfig() {
        return this.config.eventGroupsConfig;
    }

    get virtualJobMap() {
        return this.config.virtualJobMap;
    }

    get goalScorerMarketTypeToMask() {
        return this.config.goalScorerMarketTypeToMask;
    }

    get eventNameSeparators() {
        return this.config.eventNameSeparators;
    }

    getSerializableVisualConfig(PALETTE) {
        return mergeTo(sportsContentFactory(PALETTE), {
            EventMarkets: {
                initiallyExpandedMarkets: this.config.eventPageInitiallyExpandedMarketsAmount,
            },
        });
    }

    updateViewsConfig(viewsConfig) {
        const eventMarketsSwitcherGroupCategoriesMap = this.config.eventMarketsSwitcherGroupCategories.reduce(
            (acc, categoryId) => ({
                ...acc,
                [categoryId]: true,
            }),
            {}
        );

        mergeTo(viewsConfig, {
            CompetitorIconOptions: {
                enabled: this.config.teamIconsEnabled,
                enabledSports: this.config.teamIconsEnabledSports,
                disabledSports: this.config.teamIconsDisabledSports,
            },
            EventPage: {
                documentHeadMetaFactory: EventHeadMetaFactory,
            },
            SingleLeaguePage: {
                documentHeadMetaFactory: SingleLeagueHeadMetaFactory,
            },
            SportCouponPageView: {
                documentHeadMetaFactory: SportCouponHeadMetaFactory,
            },
            InPlayPage: {
                documentHeadMetaFactory: InplayHeadMetaFactory,
            },
            RaceMeetingPageView: {
                documentHeadMetaFactory: RaceMeetingHeadMetaFactory,
            },
            FutureRacePageView: {
                documentHeadMetaFactory: FutureRaceHeadMetaFactory,
            },
            SportPage: {
                documentHeadMetaFactory: SportHeadMetaFactory,
            },
            VirtualSportPageView: {
                documentHeadMetaFactory: VirtualsHeadMetaFactory,
            },
            HomePage: {
                documentHeadMetaFactory: HomePageHeadMetaFactory,
                topSportCoupons: {
                    primary: {
                        coupon: {
                            view: {
                                eventCounter: {
                                    footerSelectedSport: true,
                                },
                            },
                        },
                    },
                },
            },
            EmptyContent: {
                documentHeadMetaFactory: EmptyContentHeadMetaFactory,
            },
            PriceBoostPageView: {
                documentHeadMetaFactory: PriceBoostHeadMetaFactory,
            },
            EventMarketsGroupSwitcher: {
                marketGroupCategories: [
                    {
                        id: 'markets',
                        label: '$EVENT_MARKETS_GROUP_SWITCHER.CATEGORIES.MARKETS',
                        marketGroupFilter: marketGroup => marketGroup.type !== MARKET_GROUP_TYPES.PLAYER_PROP_BY_PLAYER,
                        combineOther: true,
                        default: false,
                        hideOneMarketGroup: true,
                    },
                    {
                        id: 'match_acca',
                        label: '$EVENT_MARKETS_GROUP_SWITCHER.CATEGORIES.MATCH_ACCA',
                        marketGroupFilter: marketGroup => marketGroup.type === MARKET_GROUP_TYPES.MATCH_ACCA,
                        default: false,
                        hideOneMarketGroup: true,
                    },
                    {
                        id: 'prebuilt_match_acca',
                        label: '$EVENT_MARKETS_GROUP_SWITCHER.CATEGORIES.PREBUILT_MATCH_ACCA',
                        marketGroupFilter: marketGroup => marketGroup.type === MARKET_GROUP_TYPES.PREBUILT_MATCH_ACCA,
                        default: false,
                        hideOneMarketGroup: true,
                    },
                    {
                        id: 'player_prop',
                        label: '$EVENT_MARKETS_GROUP_SWITCHER.CATEGORIES.PLAYER_PROP',
                        marketGroupFilter: (marketGroup, event) => {
                            const {PLAYER_PROP, PLAYER_PROP_BY_PLAYER} = MARKET_GROUP_TYPES;
                            const isPlayerPropByPlayer = groupType => groupType === PLAYER_PROP_BY_PLAYER;
                            const isPlayerProp = groupType =>
                                groupType === PLAYER_PROP || isPlayerPropByPlayer(groupType);
                            const byPlayerGroups = event.marketGroups.filter(group => isPlayerPropByPlayer(group.type));
                            const hasEnoughMarketsForPlayer = byPlayerGroups.some(group => group.marketsCount >= 3);

                            return (
                                (byPlayerGroups.length >= 3 || hasEnoughMarketsForPlayer) &&
                                isPlayerProp(marketGroup.type)
                            );
                        },
                        subCategories: [
                            {
                                id: 'per_market',
                                label: '$EVENT_MARKETS_GROUP_SWITCHER.SUB_CATEGORIES.PLAYER_PROP_PER_MARKET',
                                marketGroupFilter: marketGroup => marketGroup.type === MARKET_GROUP_TYPES.PLAYER_PROP,
                                hideOneMarketGroup: true,
                            },
                            {
                                id: 'per_player',
                                label: '$EVENT_MARKETS_GROUP_SWITCHER.SUB_CATEGORIES.PLAYER_PROP_PER_PLAYER',
                                marketGroupFilter: marketGroup =>
                                    marketGroup.type === MARKET_GROUP_TYPES.PLAYER_PROP_BY_PLAYER,
                                isComposite: true,
                                default: true,
                            },
                        ],
                        default: false,
                    },
                    {
                        id: 'fast_markets',
                        label: '$EVENT_MARKETS_GROUP_SWITCHER.CATEGORIES.FAST_MARKETS',
                        marketGroupFilter: marketGroup => marketGroup.type === MARKET_GROUP_TYPES.FAST_MARKETS,
                        default: false,
                        hideOneMarketGroup: true,
                    },
                ].filter(category => eventMarketsSwitcherGroupCategoriesMap[category.id]),
            },
        });

        super.updateViewsConfig(viewsConfig);
    }
}

export const SportsContent = new SportsContentFeature(allVisuals);
